/** Pinyin syllables with tone marks. Lesson pinyin is saved with tone marks (wǒ), which pass through;
 * rows saved before that keep tone numbers (wo3), which are converted for display. */
export function instructionalPinyin(tokens: string[] | null | undefined): string {
    const marks: Record<string, string> = { a: 'āáǎà', e: 'ēéěè', i: 'īíǐì', o: 'ōóǒò', u: 'ūúǔù', ü: 'ǖǘǚǜ' };
    const toneMarks = ['\u0304', '\u0301', '\u030c', '\u0300'];
    return (tokens ?? []).map(token => token.replace(/([a-zêü:]+)([0-5])/gi, (_match, syllable: string, tone: string) => {
        const normalized = syllable.replace(/u:|v/gi, value => value === value.toUpperCase() ? 'Ü' : 'ü');
        if (tone === '0' || tone === '5') return normalized;
        const lower = normalized.toLowerCase();
        let index = lower.indexOf('a');
        if (index < 0) index = lower.indexOf('e');
        if (index < 0 && lower.includes('ou')) index = lower.indexOf('o');
        if (index < 0) for (let i = lower.length - 1; i >= 0; i--) if (marks[lower[i]]) { index = i; break; }
        if (index < 0) {
            // No plain vowel: ê, or a syllabic n or m (嗯 ń), takes the mark.
            const base = lower.search(/[êmn]/);
            if (base < 0) return normalized + tone;
            return (normalized.slice(0, base + 1) + toneMarks[Number(tone) - 1] + normalized.slice(base + 1)).normalize('NFC');
        }
        let vowel = marks[lower[index]][Number(tone) - 1];
        if (normalized[index] !== lower[index]) vowel = vowel.toUpperCase();
        return normalized.slice(0, index) + vowel + normalized.slice(index + 1);
    })).join(' ');
}

const toneByMark: Record<string, string> = { '̄': '1', '́': '2', '̌': '3', '̀': '4' };
const isVowel = (char: string | undefined) => Boolean(char && /[aeiouvü]/i.test(char.normalize('NFC')));

/** Pinyin as typed, one syllable per space or comma; the server splits syllables written together. */
export const pinyinWords = (value: string) => (value.trim() ? value.trim().split(/[,\s]+/).filter(Boolean) : null);

const hanCount = (text: string) => (text.match(/[\u4e00-\u9fff]/g) ?? []).length;

/** A response prompt's reading from its saved sentence, which has the word in place of <Answer/>: the tag takes the place
 * of the word's syllables. Null when the syllables do not line up with the text, so which are the word's is not known. */
export function answerPromptPinyin(prompt: string, word: string, saved: string[]): string[] | null {
    const [before, after, ...more] = prompt.split(/<answer\/>/i);
    if (after === undefined || saved.some(token => /^<answer\/>$/i.test(token))) return saved;
    if (more.length || hanCount(before) + hanCount(word) + hanCount(after) !== saved.length) return null;
    return [...saved.slice(0, hanCount(before)), '<Answer/>', ...saved.slice(saved.length - hanCount(after))];
}

/** Numeric-tone text for an edit field, one syllable to each space, so wǒ is edited as wo3, a neutral-tone
 * syllable saved without a mark (ma) as ma5, and a word printed as one (jiǎozi) as jiao3 zi5. Each tone number
 * goes after its syllable's vowels and any final n, ng or r that does not start the next syllable. Text that is
 * not pinyin keeps its letters. The server saves the numbers as marks. */
export function editablePinyin(tokens: string[] | null | undefined): string {
    return (tokens ?? []).map(token => {
        const chars = Array.from(token.normalize('NFD'));
        let out = '';
        for (let i = 0; i < chars.length; i++) {
            const tone = toneByMark[chars[i + 1]];
            if (!tone) { out += chars[i]; continue; }
            out += chars[i];
            i += 2;
            while (i < chars.length && (isVowel(chars[i]) || chars[i] === '̈') && !toneByMark[chars[i + 1]]) out += chars[i++];
            for (const finals of [['ng', 'n'], ['r']]) for (const final of finals) {
                const next = chars.slice(i, i + final.length).join('');
                if (next.toLowerCase() === final && !isVowel(chars[i + final.length])) { out += next; i += final.length; break; }
            }
            out += tone;
            i--;
        }
        out = out.normalize('NFC');
        return numberedSyllables(out) ?? out;
    }).join(' ');
}

/** Pinyin with tone numbers, one numbered syllable to each space (ma3mahu1hu1 is ma3 ma5 hu1 hu1); null if it is not pinyin. */
function numberedSyllables(written: string): string | null {
    const runs = Array.from(written.matchAll(/([a-zü:]+)([0-5]?)/gi));
    if (runs.map(run => run[0]).join('') !== written.replace(/['’-]/g, '')) return null;
    const numbered: string[] = [];
    for (const [, letters, tone] of runs) {
        const found = syllables(letters);
        if (!found) return null;
        found.forEach((syllable, index) => numbered.push(syllable + ((index === found.length - 1 && tone) || '5')));
    }
    return numbered.join(' ');
}

const isSyllable = (letters: string) => pinyinSyllable.test(letters.toLowerCase().replace(/u:|v/g, 'ü'));

/** Letters without tones as their syllables, each as long as the rest still allows; null if they are not all syllables.
 * Only the first may start with a vowel, as pinyin writes an apostrophe before a later one: fanguan is fan guan. */
function syllables(letters: string): string[] | null {
    const from: (string[] | null)[] = [...Array<null>(letters.length).fill(null), []];
    for (let at = letters.length - 1; at >= 0; at--) {
        for (let end = Math.min(at + 7, letters.length); end > at && !from[at]; end--) {
            const piece = letters.slice(at, end);
            const rest = from[end];
            if (rest && isSyllable(piece) && (at === 0 || !/^[aeiouü]/i.test(piece))) from[at] = [piece, ...rest];
        }
    }
    return from[0];
}

const pinyinSyllable = /^(?:zh|ch|sh|[bpmfdtnlgkhjqxrzcsyw])?(?:a|ai|an|ang|ao|e|ei|en|eng|er|i|ia|ian|iang|iao|ie|in|ing|iong|iu|o|ong|ou|u|ua|uai|uan|uang|ue|ui|un|uo|ü|üe|üan|ün)r?$/;

/** Tone marks for numeric-tone pinyin inside free text such as plan sections and materials, for
 * text saved before the server converted it. A run is changed only when every syllable in it is
 * real pinyin, so "15 yuan", "mp3" or a level such as "A1" stays as written. The server's
 * tone_marked_text() applies the same rule when lesson text is saved. */
export function instructionalPinyinText(text: string | null | undefined): string {
    return (text ?? '').replace(/(?<![A-Za-zü:0-9])(?:[A-Za-zü:]+[1-5])+(?![A-Za-zü:0-9])/g, run => {
        if (run === run.toUpperCase()) return run;
        const syllables = Array.from(run.matchAll(/([A-Za-zü:]+)[1-5]/g), match => match[1].toLowerCase().replace(/u:|v/g, 'ü'));
        return syllables.every(syllable => pinyinSyllable.test(syllable)) ? instructionalPinyin([run]) : run;
    });
}
